import { Box, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import React from 'react';
import { protoIcon } from '../common/ProtoIcon';

const MoreVertical = protoIcon('dots');

/**
 * Slot de ações do AppBar mobile (`MobileLayout`). A tela registra uma ação
 * com `useAppBarAction`; o layout a renderiza à esquerda do botão de tema.
 * Fora do `MobileLayout` (desktop) o contexto é no-op.
 *
 * Duas formas: uma ação única (`onClick`) ou um menu "mais" (`menu`), que
 * renderiza o botão ⋯ abrindo uma lista de itens.
 */
const AppBarActionsContext = React.createContext(() => {});

export const AppBarActionsProvider = AppBarActionsContext.Provider;

// `.icon-btn` do protótipo: 40x40, raio 12, ícone text-2; com `dot`, ponto vermelho 8px (borda 2px da superfície).
export const appBarButtonSx = {
    width: 40,
    height: 40,
    flexShrink: 0,
    border: 'none',
    borderRadius: '12px',
    background: 'transparent',
    color: 'text.secondary',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    padding: 0,
    cursor: 'pointer',
    '&:active': { backgroundColor: 'background.subtle' },
    '&:disabled': { opacity: 0.6, cursor: 'default' },
};

// Ponto de notificação (`.icon-btn .dot`).
export function AppBarDot() {
    return (
        <Box
            component="span"
            sx={{ position: 'absolute', top: '8px', right: '9px', width: 8, height: 8, borderRadius: '50%', backgroundColor: 'error.main', border: '2px solid', borderColor: 'background.paper', boxSizing: 'border-box' }}
        />
    );
}

// Botão ⋯ que abre um Menu ancorado com os itens `{ label, icon, onClick, disabled, destructive, title }`.
function AppBarMenuButton({ menu }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const close = () => setAnchorEl(null);

    return (
        <>
            <Box
                component="button"
                type="button"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                aria-label="Mais ações"
                aria-haspopup="true"
                sx={appBarButtonSx}
            >
                <MoreVertical size={20} />
            </Box>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {menu.map(({ label, icon: Icon, onClick, disabled, destructive, title }) => (
                    <MenuItem
                        key={label}
                        disabled={disabled}
                        title={title || undefined}
                        onClick={() => {
                            close();
                            if (onClick) onClick();
                        }}
                        sx={destructive ? { color: 'error.main' } : undefined}
                    >
                        {Icon && (
                            <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                                <Icon size={18} />
                            </ListItemIcon>
                        )}
                        <ListItemText primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}>{label}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

// Botão no padrão dos botões do AppBar (44x44, ícone branco, fundo transparente).
export function AppBarActionButton({ label, icon: Icon, onClick, disabled, spinning, menu, dot, actions }) {
    // Várias ações lado a lado (ex.: Filtros + Próximos serviços na lista de ordens).
    if (actions && actions.length > 0) {
        return (
            <>
                {actions.map((action) => <AppBarActionButton key={action.label} {...action} />)}
            </>
        );
    }
    if (menu && menu.length > 0) return <AppBarMenuButton menu={menu} />;
    if (!Icon) return null;
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label={label}
            sx={{
                ...appBarButtonSx,
                '@keyframes appBarActionSpin': { to: { transform: 'rotate(360deg)' } },
                '& svg': spinning ? { animation: 'appBarActionSpin 1s linear infinite' } : undefined,
            }}
        >
            <Icon size={20} />
            {dot && <AppBarDot />}
        </Box>
    );
}

/**
 * Registra uma ação no AppBar mobile enquanto a tela estiver montada.
 * `enabled=false` (ex.: desktop) não registra nada. `icon` deve ser um
 * componente estável (ex.: ícone do lucide-react).
 *
 * `menu` (opcional): `[{ label, icon, onClick, disabled, destructive, title }]`.
 * Com itens, o AppBar mostra o botão ⋯ em vez da ação única; lista vazia não
 * registra nada. Os `onClick` são lidos via ref (podem mudar a cada render sem
 * re-registrar); só label/disabled/destructive/title entram na chave de
 * dependência. `icon` dos itens deve ser estável por `label`.
 */
export function useAppBarAction({ label, icon, onClick, disabled = false, spinning = false, enabled = true, menu, dot = false, actions }) {
    const setAction = React.useContext(AppBarActionsContext);
    const onClickRef = React.useRef(onClick);
    onClickRef.current = onClick;
    const menuRef = React.useRef(menu);
    menuRef.current = menu;

    const actionsRef = React.useRef(actions);
    actionsRef.current = actions;
    const hasActions = Array.isArray(actions);
    const actionsKey = hasActions
        ? actions.map((i) => [i.label, i.disabled ? 1 : 0, i.dot ? 1 : 0].join('|')).join(';')
        : '';

    const hasMenu = Array.isArray(menu);
    const menuKey = hasMenu
        ? menu.map((i) => [i.label, i.disabled ? 1 : 0, i.destructive ? 1 : 0, i.title || ''].join('|')).join(';')
        : '';

    React.useEffect(() => {
        if (!enabled) return undefined;
        if (hasActions) {
            const current = actionsRef.current || [];
            if (current.length === 0) return undefined;
            setAction({
                actions: current.map((item, index) => ({
                    label: item.label,
                    icon: item.icon,
                    disabled: Boolean(item.disabled),
                    dot: Boolean(item.dot),
                    onClick: () => {
                        const target = actionsRef.current && actionsRef.current[index];
                        if (target && target.onClick) target.onClick();
                    },
                })),
            });
            return () => setAction(null);
        }
        if (hasMenu) {
            const current = menuRef.current || [];
            if (current.length === 0) return undefined;
            setAction({
                menu: current.map((item, index) => ({
                    label: item.label,
                    icon: item.icon,
                    disabled: Boolean(item.disabled),
                    destructive: Boolean(item.destructive),
                    title: item.title,
                    onClick: () => {
                        const target = menuRef.current && menuRef.current[index];
                        if (target && target.onClick) target.onClick();
                    },
                })),
            });
            return () => setAction(null);
        }
        setAction({ label, icon, disabled, spinning, dot, onClick: () => onClickRef.current && onClickRef.current() });
        return () => setAction(null);
    }, [enabled, label, icon, disabled, spinning, dot, setAction, hasMenu, menuKey, hasActions, actionsKey]);
}

/**
 * Subtítulo do AppBar mobile (`.ab-sub`, 11.5px text-3 sob o título). A tela o
 * registra com `useAppBarSubtitle('texto')`; `null`/vazio remove. Fora do
 * `MobileLayout` (desktop) o contexto é no-op.
 */
const AppBarSubtitleContext = React.createContext(() => {});

export const AppBarSubtitleProvider = AppBarSubtitleContext.Provider;

export function useAppBarSubtitle(text) {
    const setSubtitle = React.useContext(AppBarSubtitleContext);
    React.useEffect(() => {
        if (!text) return undefined;
        setSubtitle(text);
        return () => setSubtitle(null);
    }, [text, setSubtitle]);
}

/**
 * Título do AppBar mobile quando ele depende de dados da tela (ex.: "Venda #8814").
 * A tela registra com `useAppBarTitle('texto')`; vazio/`null` volta ao título padrão
 * do menu. Fora do `MobileLayout` (desktop) o contexto é no-op.
 */
const AppBarTitleContext = React.createContext(() => {});

export const AppBarTitleProvider = AppBarTitleContext.Provider;

export function useAppBarTitle(text) {
    const setTitle = React.useContext(AppBarTitleContext);
    React.useEffect(() => {
        if (!text) return undefined;
        setTitle(text);
        return () => setTitle(null);
    }, [text, setTitle]);
}
